import { useToast } from "@chakra-ui/react";
import { isAxiosError } from "axios";
import { ReactNode, useRef, useState } from "react";

import api from "@/common/utils/api";
import type { ErrorResponse } from "@reflag/shared/api";

const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
const maximumFileSize = 2 * 1024 * 1024;

type UploadLogoButtonProps = {
  orgId: string;
  children: (props: { onClick: () => void }) => ReactNode;
  onChange: (fileUrl: string) => void;
};

export function UploadLogoButton({
  orgId,
  children,
  onChange,
}: UploadLogoButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const toast = useToast();

  const showError = (description: string) => {
    toast({ status: "error", title: "Could not upload logo", description });
  };

  const upload = async (file: File) => {
    if (!allowedTypes.includes(file.type)) {
      showError("Choose a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > maximumFileSize) {
      showError("The image must be 2 MB or smaller.");
      return;
    }

    setIsUploading(true);
    try {
      const body = await file.arrayBuffer();
      const response = await api.post<"/orgs/:orgId/logo">(
        `/orgs/${orgId}/logo`,
        body,
        { headers: { "Content-Type": file.type } },
      );
      onChange(response.data.logoUrl);
    } catch (error: unknown) {
      showError(
        (isAxiosError<ErrorResponse>(error) &&
          error.response?.data?.error?.message) ||
          "Please try again.",
      );
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        hidden
        accept={allowedTypes.join(",")}
        disabled={isUploading}
        type="file"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void upload(file);
        }}
      />
      {children({
        onClick: () => {
          if (!isUploading) inputRef.current?.click();
        },
      })}
    </>
  );
}
